import { autofocus } from "@solid-primitives/autofocus";
import { getAuth } from "firebase/auth";
import {
  Component,
  createEffect,
  createSignal,
  createUniqueId,
  For,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
} from "solid-js";
import { Portal } from "solid-js/web";

import { useToast } from "../toast/index.js";
import { UserSearch } from "./UserSearch.js";
// prevent TS from tree shaking this
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
autofocus;

export const CommandBar: Component<{ show: boolean; onClose: () => void }> = (
  props,
) => {
  const auth = getAuth();
  const toast = useToast();

  const [activeAction, _setActiveAction] = createSignal<string | null>(null);

  const setActiveAction = (actionKey: string | null) => {
    if (actionKey === "copy-auth-token") {
      auth.currentUser
        ?.getIdToken()
        .then((token) => navigator.clipboard.writeText(token));
      toast.success("Success", "Auth token copied to clipboard");
      close();
      return;
    }
    _setActiveAction(actionKey);
  };

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  const close = () => {
    props.onClose();
    setActiveAction(null);
  };

  return (
    <Show when={props.show}>
      <Portal>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          class="fixed inset-0 z-50 flex items-center justify-center"
          // TODO: Should we remove this for better a11y?
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              props.onClose();
            }
          }}
          onKeyDown={handleEscapeKeyDown}
        >
          <div
            role="dialog"
            aria-labelledby="modal-title"
            class="mx-4 max-h-full w-full max-w-2xl rounded-lg border bg-white p-5 shadow-lg"
          >
            <Show
              when={activeAction()}
              fallback={<SelectActionView onChange={setActiveAction} />}
            >
              <Switch>
                <Match when={activeAction()?.startsWith("user-search")}>
                  <UserSearch
                    actionKey={activeAction()!}
                    onComplete={props.onClose}
                    onBack={() => setActiveAction(null)}
                  />
                </Match>
              </Switch>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
};

const SelectActionView: Component<{ onChange: (actionKey: string) => void }> = (
  props,
) => {
  const [actionQuery, setActionQuery] = createSignal<null | string>(null);
  const [activeOptionIndex, setActiveOptionIndex] = createSignal<number>(-1);

  let inputElm!: HTMLInputElement;
  const scrollableContainerId = createUniqueId();

  const actions = [
    {
      name: "Go to applicant profile",
      shortcut: "ga",
      actionKey: "user-search-applicant",
    },
    {
      name: "Go to candidate profile",
      shortcut: "gc",
      actionKey: "user-search-candidate",
    },
    {
      name: "Go to pre-screen candidate profile",
      shortcut: "gs",
      actionKey: "user-search-staged-candidate",
    },
    {
      name: "Search by onboarding user ID",
      shortcut: "so",
      actionKey: "user-search-onboarding",
    },
    {
      name: "Search by name",
      shortcut: "sn",
      actionKey: "user-search-name",
    },
    {
      name: "Search by email address",
      shortcut: "se",
      actionKey: "user-search-email",
    },
    {
      name: "Search by phone number",
      shortcut: "sp",
      actionKey: "user-search-phone",
    },
    {
      name: "Copy auth token",
      actionKey: "copy-auth-token",
      shortcut: "ct",
    },
  ];

  const filteredActions = () => {
    const query = actionQuery();

    if (!query?.length) return actions;

    return actions.filter(
      (action) =>
        action.name.toLowerCase().includes(query.toLowerCase()) ||
        action.shortcut.toLowerCase().includes(query.toLowerCase()),
    );
  };

  const onKeyUp = (event: KeyboardEvent) => {
    event.preventDefault();
    switch (event.key) {
      case "ArrowDown": {
        setActiveOptionIndex(
          Math.min(activeOptionIndex() + 1, filteredActions().length - 1),
        );
        break;
      }
      case "ArrowUp": {
        setActiveOptionIndex(Math.max(activeOptionIndex() - 1, 0));
        break;
      }
      case "Enter": {
        if (activeOptionIndex() >= 0) {
          props.onChange(filteredActions()[activeOptionIndex()]?.actionKey);
        } else if (filteredActions().length === 1) {
          props.onChange(filteredActions().at(0)!.actionKey);
        }
        break;
      }
    }
  };

  createEffect(() => {
    const index = activeOptionIndex();
    const container = document.getElementById(scrollableContainerId);
    const activeOption = container?.childNodes.item(index) as
      | HTMLElement
      | undefined;
    if (activeOption && container) {
      if (container.scrollTop > activeOption.offsetTop) {
        container.scrollTop = activeOption.offsetTop;
      }

      if (
        container.scrollTop +
          container.clientHeight -
          activeOption.clientHeight <=
        activeOption.offsetTop
      ) {
        container.scrollTop =
          activeOption.offsetTop +
          activeOption.clientHeight -
          container.clientHeight;
      }
    }
  });

  onMount(() => {
    document.addEventListener("keyup", onKeyUp);
  });

  onCleanup(() => {
    document.removeEventListener("keyup", onKeyUp);
  });

  return (
    <div class="space-y-1">
      <input
        use:autofocus
        autofocus
        ref={inputElm!}
        placeholder="What do you want to do?"
        class="w-full p-4 text-lg"
        onKeyUp={(e) => setActionQuery(e.currentTarget.value)}
      />

      <div
        id={scrollableContainerId}
        class="relative h-[25vh] space-y-1 overflow-y-auto"
        style={{
          "scrollbar-color": `rgb(217 215 253) transparent`,
          "scrollbar-width": "thin",
        }}
      >
        <For each={filteredActions()}>
          {({ name, shortcut, actionKey }, index) => (
            <button
              class="w-full rounded-md p-4 text-left hover:bg-gray-100"
              classList={{
                "bg-primary-200 hover:bg-primary-300":
                  index() === activeOptionIndex(),
              }}
              onClick={() => props.onChange(actionKey)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  props.onChange(actionKey);
                }
              }}
            >
              <div class="flex items-center justify-between">
                <span>{name}</span>
                <span class="text-sm text-gray-500">{shortcut}</span>
              </div>
            </button>
          )}
        </For>
      </div>
    </div>
  );
};
